/**
 * Global Intercom Trigger System
 *
 * Usage from anywhere in the app:
 *
 * // Basic trigger
 * window.dispatchEvent(new Event("openIntercom"));
 *
 * // Trigger with delay
 * window.dispatchEvent(new CustomEvent("openIntercom", {
 *   detail: { delay: 5000 }
 * }));
 *
 * // Trigger with custom options
 * window.dispatchEvent(new CustomEvent("openIntercom", {
 *   detail: {
 *     delay: 20000,
 *     reason: "login_success",
 *     excludePaths: ["/dashboard"]
 *   }
 * }));
 */

export enum IntercomTriggerReason {
  LOGIN_SUCCESS = "login_success",
  FAILED_LOGIN_ATTEMPTS = "failed_login_attempts",
  MANUAL = "manual",
  PAGE_LOAD = "page_load",
  USER_ACTION = "user_action",
  DAY_1_AUTO = "day_1_auto_trigger",
  DAY_2_AUTO = "day_2_auto_trigger",
  DAY_3_AUTO = "day_3_auto_trigger",
}

export interface IntercomTriggerOptions {
  delay?: number; // delay in milliseconds
  reason?: IntercomTriggerReason | string;
  excludePaths?: string[]; // paths where intercom should not trigger
}

// LocalStorage keys
const INTERCOM_VISIT_DATES_KEY = "intercom_visit_dates";
const INTERCOM_LAST_TRIGGER_DATE_KEY = "intercom_last_auto_trigger_date";
const INTERCOM_NEW_USER_INITIALIZED_KEY = "intercom_new_user_initialized";

// SessionStorage key
const INTERCOM_MANUAL_SESSION_KEY = "intercom_manually_opened_this_session";

/**
 * Get today's date in YYYY-MM-DD format
 */
export const getTodayDateString = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Get array of visit dates from localStorage
 */
const getVisitDates = (): string[] => {
  try {
    const stored = localStorage.getItem(INTERCOM_VISIT_DATES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

/**
 * Save visit dates to localStorage
 */
const setVisitDates = (dates: string[]): void => {
  try {
    localStorage.setItem(INTERCOM_VISIT_DATES_KEY, JSON.stringify(dates));
  } catch (error) {
    console.error("Failed to save visit dates:", error);
  }
};

/**
 * Get the number of unique days user has visited the app
 */
export const getIntercomDayCount = (): number => {
  return getVisitDates().length;
};

/**
 * Record today's visit if not already recorded
 */
export const recordTodayVisit = (): void => {
  const today = getTodayDateString();
  const visitDates = getVisitDates();

  if (!visitDates.includes(today)) {
    visitDates.push(today);
    setVisitDates(visitDates);
  }
};

/**
 * Check if we already triggered Intercom today
 */
export const hasTriggeredIntercomToday = (): boolean => {
  try {
    const lastTriggerDate = localStorage.getItem(
      INTERCOM_LAST_TRIGGER_DATE_KEY
    );
    const today = getTodayDateString();
    return lastTriggerDate === today;
  } catch {
    return false;
  }
};

/**
 * Mark that we triggered Intercom today
 */
export const markIntercomTriggeredToday = (): void => {
  try {
    const today = getTodayDateString();
    localStorage.setItem(INTERCOM_LAST_TRIGGER_DATE_KEY, today);
  } catch (error) {
    console.error("Failed to mark Intercom trigger:", error);
  }
};

/**
 * Initialize Intercom tracking for a new user (reset all tracking)
 */
export const initializeNewUserIntercomTracking = (): void => {
  try {
    const today = getTodayDateString();
    setVisitDates([today]);
    localStorage.setItem(INTERCOM_LAST_TRIGGER_DATE_KEY, "");
    localStorage.setItem(INTERCOM_NEW_USER_INITIALIZED_KEY, "true");
  } catch (error) {
    console.error("Failed to initialize new user tracking:", error);
  }
};

/**
 * Check if user manually opened Intercom this session
 */
export const hasManuallyOpenedThisSession = (): boolean => {
  try {
    return sessionStorage.getItem(INTERCOM_MANUAL_SESSION_KEY) === "true";
  } catch {
    return false;
  }
};

/**
 * Mark that user manually opened Intercom this session
 */
export const markManuallyOpenedThisSession = (): void => {
  try {
    sessionStorage.setItem(INTERCOM_MANUAL_SESSION_KEY, "true");
  } catch (error) {
    console.error("Failed to mark manual open:", error);
  }
};

/**
 * Get delay in milliseconds for current day, or null if shouldn't trigger
 * Day 1: 60 seconds (1 min)
 * Day 2: 120 seconds (2 min)
 * Day 3: 180 seconds (3 min)
 * Day 4+: null (no auto-trigger)
 */
export const getIntercomDelayForToday = (): number | null => {
  const dayCount = getIntercomDayCount();

  switch (dayCount) {
    case 1:
      return 60000; // 1 minute
    case 2:
      return 120000; // 2 minutes
    case 3:
      return 180000; // 3 minutes
    default:
      return null; // No auto-trigger for day 4+
  }
};

/**
 * Get reason string for current day's auto-trigger
 */
export const getIntercomReasonForToday = (): IntercomTriggerReason => {
  const dayCount = getIntercomDayCount();

  switch (dayCount) {
    case 1:
      return IntercomTriggerReason.DAY_1_AUTO;
    case 2:
      return IntercomTriggerReason.DAY_2_AUTO;
    case 3:
      return IntercomTriggerReason.DAY_3_AUTO;
    default:
      return IntercomTriggerReason.MANUAL;
  }
};

// Global trigger functions that can be called from anywhere
export const triggerIntercom = (options: IntercomTriggerOptions = {}) => {
  window.dispatchEvent(new CustomEvent("openIntercom", { detail: options }));
};

export const triggerIntercomOnLoginSuccess = () => {
  triggerIntercom({
    delay: 210000, // 210 seconds
    reason: IntercomTriggerReason.LOGIN_SUCCESS,
    excludePaths: ["#/dashboard?patientIds"],
  });
};

export const triggerIntercomOnFailedLogin = (attemptCount: number) => {
  if (attemptCount >= 2) {
    triggerIntercom({
      delay: 1000, // 1 second
      reason: IntercomTriggerReason.FAILED_LOGIN_ATTEMPTS,
    });
  }
};

export const triggerIntercomOnPageLoad = (delayMs: number = 30000) => {
  triggerIntercom({
    delay: delayMs,
    reason: IntercomTriggerReason.PAGE_LOAD,
    excludePaths: ["#/dashboard?patientIds"],
  });
};

// Utility function to check if current path should be excluded
export const shouldExcludePath = (excludePaths: string[] = []) => {
  const currentPath = window.location.hash || window.location.pathname;
  return excludePaths.some((path) => currentPath.includes(path));
};
